import { SKIPPED_FIELDS_KEY } from '../../../util/constants';
import { checkFieldVisibility } from '../../dynamicFormEngine/utils/dependsOnValidationService';

const READONLY_FIELD_TYPES = [
  'YEAR_PICKER',
  'INLINE_TEXT_FIELD',
  'MULTISELECT_DROP_DOWN',
  'DROP_DOWN',
  'RADIO_BUTTONS',
  'VIDEO_LINK',
  'DOCUMENT_LINK',
  'CHECK_BOX_GROUP',
  'CHECK_BOX',
  'SLIDER',
  'COLOUR_SLIDER',
  'PHONE_INPUT',
  'LABEL',
  'FILE_UPLOAD',
  'SIGNATURE',
  'DATA_LABEL',
];

const collectFields = (fields, categoryKey, matches) => {
  if (!Array.isArray(fields)) return;
  fields.forEach((field) => {
    if (field?.key) {
      matches.push({ field, categoryKey });
    }
    if (Array.isArray(field.fields)) {
      collectFields(field.fields, categoryKey, matches);
    }
  });
};

/**
 * Find a field definition within a read-only form section (categories + root fields).
 */
export const findFieldInFormSection = (sectionDetails, fieldKey, categoryKey = null) => {
  if (!sectionDetails || !fieldKey) return null;

  const matches = [];

  if (Array.isArray(sectionDetails.fields)) {
    collectFields(sectionDetails.fields, null, matches);
  }

  if (Array.isArray(sectionDetails.categories)) {
    sectionDetails.categories.forEach((category) => {
      if (Array.isArray(category.fields)) {
        collectFields(category.fields, category.key, matches);
      }
    });
  }

  const filtered = matches.filter(({ field, categoryKey: catKey }) => field.key === fieldKey);
  if (filtered.length === 0) return null;

  if (categoryKey) {
    const exact = filtered.find(({ categoryKey: catKey }) => catKey === categoryKey);
    if (exact) return exact;
  }

  if (filtered.length === 1) return filtered[0];
  return filtered[0];
};

const hasReadOnlyFieldAccess = (field, userSiteTrialRole) => {
  return (
    field.type === 'SIGNATURE' ||
    !field.access ||
    (field.access && field.access === userSiteTrialRole)
  );
};

const isCategoryVisibleInReadOnly = (category, answers) => {
  if (!category) return true;
  if (SKIPPED_FIELDS_KEY.includes(category.key)) return false;
  if (category.showInReadOnly !== undefined && !category.showInReadOnly) return false;
  return checkFieldVisibility(category, answers, true);
};

/**
 * Mirrors FieldList visibility rules for read-only CRF fields.
 */
export const isFieldVisibleInReadOnlyView = (field, answers, userSiteTrialRole, category = null) => {
  if (!field) return false;
  if (!READONLY_FIELD_TYPES.includes(field.type)) return false;
  if (SKIPPED_FIELDS_KEY.includes(field.key)) return false;
  if (!isCategoryVisibleInReadOnly(category, answers)) return false;
  if (!checkFieldVisibility(field, answers, true)) return false;
  return hasReadOnlyFieldAccess(field, userSiteTrialRole);
};

const normalizeDisplayLabel = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const forEachFormSection = (formConfig, visitSection) => {
  const sections = [];
  if (visitSection) {
    sections.push(visitSection);
  } else if (formConfig?.sections) {
    sections.push(...formConfig.sections);
  } else if (formConfig?.steps) {
    formConfig.steps.forEach((step) => {
      if (Array.isArray(step.sections)) {
        sections.push(...step.sections);
      }
    });
  }
  return sections;
};

const findFieldLabelInSections = (sections, fieldKey, categoryKey = null) => {
  if (!fieldKey || !sections?.length) return null;
  for (const section of sections) {
    const resolved = findFieldInFormSection(section, fieldKey, categoryKey);
    const label = normalizeDisplayLabel(resolved?.field?.label);
    if (label) return label;
  }
  if (categoryKey) return null;
  for (const section of sections) {
    const resolved = findFieldInFormSection(section, fieldKey);
    const label = normalizeDisplayLabel(resolved?.field?.label);
    if (label) return label;
  }
  return null;
};

const findCategoryInSections = (sections, categoryKey) => {
  if (!categoryKey || !sections?.length) return null;
  for (const section of sections) {
    const category = section.categories?.find((c) => c.key === categoryKey);
    if (category) return { category, section };
  }
  return null;
};

export const resolveFieldDisplayLabel = (field, formConfig, sectionDetails, categoryKey) => {
  const direct = normalizeDisplayLabel(field?.label);
  if (direct) return direct;

  const sections = forEachFormSection(formConfig, sectionDetails);
  const fromConfig = findFieldLabelInSections(sections, field?.key, categoryKey);
  if (fromConfig) return fromConfig;

  return 'This field';
};

export const resolveCategoryDisplayLabel = (category, sectionDetails, formConfig, categoryKey) => {
  const direct = normalizeDisplayLabel(category?.label);
  if (direct) return direct;

  const helper = normalizeDisplayLabel(category?.helperLabel);
  if (helper) return helper;

  const sections = forEachFormSection(formConfig, sectionDetails);
  const key = category?.key || categoryKey;
  if (key) {
    const located = findCategoryInSections(sections, key);
    const locatedLabel = normalizeDisplayLabel(located?.category?.label);
    if (locatedLabel) return locatedLabel;
    const locatedHelper = normalizeDisplayLabel(located?.category?.helperLabel);
    if (locatedHelper) return locatedHelper;
    const sectionLabel = normalizeDisplayLabel(located?.section?.label);
    if (sectionLabel) return sectionLabel;
  }

  const sectionLabel = normalizeDisplayLabel(sectionDetails?.label);
  if (sectionLabel) return sectionLabel;

  return 'this form section';
};

const resolveControllingFieldLabel = (dependsOn, formConfig, sectionDetails, categoryKey) => {
  if (!dependsOn) return null;

  const sections = forEachFormSection(formConfig, sectionDetails);
  const pathParts = dependsOn.split('.');
  const fieldKey = pathParts[pathParts.length - 1];
  const pathCategoryKey = pathParts.length > 1 ? pathParts[0] : categoryKey;

  return findFieldLabelInSections(sections, fieldKey, pathCategoryKey);
};

/**
 * User-facing message when the queried field is not rendered on the read-only form.
 */
export const getHiddenFieldNotice = (
  field,
  answers,
  userSiteTrialRole,
  category,
  sectionDetails,
  formConfig = null,
  categoryKey = null
) => {
  const resolvedCategoryKey = categoryKey || category?.key || null;
  const fieldLabel = resolveFieldDisplayLabel(field, formConfig, sectionDetails, resolvedCategoryKey);

  if (category && !isCategoryVisibleInReadOnly(category, answers)) {
    const categoryLabel = resolveCategoryDisplayLabel(
      category,
      sectionDetails,
      formConfig,
      resolvedCategoryKey
    );
    return `The field "${fieldLabel}" belongs to "${categoryLabel}", which is hidden on the form with the current data. You can still view and manage queries below.`;
  }

  if (field?.dependsOn && !checkFieldVisibility(field, answers, true)) {
    const controllingLabel = resolveControllingFieldLabel(
      field.dependsOn,
      formConfig,
      sectionDetails,
      resolvedCategoryKey
    );
    if (controllingLabel) {
      return `The field "${fieldLabel}" is hidden on the form because of "${controllingLabel}". You can still view and manage queries below.`;
    }
    return `The field "${fieldLabel}" is hidden on the form based on current visibility rules. You can still view and manage queries below.`;
  }

  if (!checkFieldVisibility(field, answers, true)) {
    return `The field "${fieldLabel}" is hidden on the form based on current visibility rules. You can still view and manage queries below.`;
  }

  if (field?.access && !hasReadOnlyFieldAccess(field, userSiteTrialRole)) {
    return `The field "${fieldLabel}" is not shown in read-only view for your role. You can still view and manage queries below.`;
  }

  return `The field "${fieldLabel}" is not shown in read-only view. You can still view and manage queries below.`;
};

export const getFieldAnswerValue = (answers, categoryKey, fieldKey) => {
  if (!answers || !fieldKey) return undefined;
  if (categoryKey && answers[categoryKey] != null) {
    return answers[categoryKey][fieldKey];
  }
  return answers[fieldKey];
};
